import { useState } from "react";
import { DownloadSimple, UploadSimple, FileXls, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, download } from "../lib/api";
import { Modal, DataTable } from "./kit";

const TONE = { new: "text-emerald-300 bg-emerald-500/10 border-emerald-400/30", update: "text-blue-300 bg-blue-500/10 border-blue-400/30",
  unchanged: "text-zinc-400 bg-white/5 border-white/10", error: "text-rose-300 bg-rose-500/10 border-rose-400/30" };
const LABEL = { new: "New", update: "Update", unchanged: "No change", error: "Error" };

const ActionTag = ({ a }) => <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${TONE[a]}`}>{LABEL[a]}</span>;

function Summary({ s }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {["new", "update", "unchanged", "error"].map((k) => (
        <div key={k} className="rounded-2xl border border-white/[0.07] bg-white/[0.03] p-3" data-testid={`import-summary-${k}`}>
          <div className="text-[11px] text-zinc-400">{LABEL[k]}</div>
          <div className={`mt-1 font-display text-2xl font-semibold tabular-nums ${TONE[k].split(" ")[0]}`}>{s[k]}</div>
        </div>
      ))}
    </div>
  );
}

const detail = (r) => (r.errors.length ? <span className="text-rose-300">{r.errors.join("; ")}</span>
  : <span className="text-zinc-400">{[r.changes.length ? `Changes: ${r.changes.join(", ")}` : "", r.still_missing?.length ? `Still missing: ${r.still_missing.join(", ")}` : ""].filter(Boolean).join(" · ") || "—"}</span>);

const RowsTable = ({ rows }) => (
  <div className="max-h-[320px] overflow-y-auto">
    <DataTable testid="import-preview-table" minWidth={760} head={["Row", "Action", "Admission no", "Name", "Class", "Details"]}
      rows={rows.map((r) => [String(r.row), <ActionTag a={r.action} />, r.admission_no || "—", r.name || "—", r.class_name || "—", <div className="whitespace-normal">{detail(r)}</div>])} />
  </div>
);

function saveCreds(list) {
  const head = ["Student", "Admission no", "Class", "Parent login", "Parent password", "Student login", "Student password"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [head, ...list.map((c) => [c.name, c.admission_no, c.class_name, c.parent_email, c.parent_password, c.student_email, c.student_password])].map((r) => r.map(esc).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: "text/csv" }));
  a.download = "new_student_logins.csv";
  a.click();
}

export function BulkImport({ open, setOpen, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const reset = () => { setFile(null); setPreview(null); setResult(null); };
  const send = async (path) => {
    const fd = new FormData();
    fd.append("file", file);
    return (await api.post(path, fd)).data;
  };
  const run = async (commit) => {
    setBusy(true);
    try {
      if (commit) {
        const r = await send("/student-import/commit");
        setResult(r);
        toast.success(`${r.summary.new} added · ${r.summary.update} updated`);
        onDone();
      } else setPreview(await send("/student-import/preview"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const tpl = async (prefill) => {
    try {
      await download(`/student-import/template?prefill=${prefill}`, prefill === "all" ? "students_update_sheet.xlsx" : "student_import_template.xlsx");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const valid = preview ? preview.summary.new + preview.summary.update : 0;
  return (
    <Modal open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }} wide testid="bulk-import-dialog" title="Bulk import students"
      description="Add new students or update existing ones (matched by admission number) from an Excel sheet.">
      {result ? (
        <div className="space-y-5" data-testid="import-result">
          <div className="flex items-center gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200"><CheckCircle size={20} weight="fill" /> Import finished.</div>
          <Summary s={result.summary} />
          {result.credentials.length > 0 && (
            <button className="btn-primary" onClick={() => saveCreds(result.credentials)} data-testid="import-download-logins">
              <DownloadSimple size={16} /> {`Download ${result.credentials.length} new login(s)`}</button>
          )}
          {result.summary.error > 0 && <RowsTable rows={result.rows.filter((r) => r.action === "error")} />}
          <button className="btn-ghost" onClick={reset} data-testid="import-another">Import another file</button>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-2">
            <button className="btn-ghost !text-xs" onClick={() => tpl("none")} data-testid="import-template-btn"><FileXls size={15} /> Blank template</button>
            <button className="btn-ghost !text-xs" onClick={() => tpl("all")} data-testid="import-current-btn"><FileXls size={15} /> Current students (to update)</button>
          </div>
          <ul className="space-y-1 text-xs text-zinc-400">
            <li>• Leave Admission No blank to add a new student. Their parent and student logins are created automatically.</li>
            <li>• Fill in Admission No to update that student. Only filled cells change, and empty cells keep the current value.</li>
          </ul>
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-white/15 bg-white/[0.02] px-4 py-8 text-center transition-colors duration-200 hover:bg-white/[0.05]">
            <UploadSimple size={26} className="text-blue-300" />
            <span className="text-sm text-white" data-testid="import-file-name">{file ? file.name : "Choose an .xlsx or .csv file"}</span>
            <input type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => { setFile(e.target.files[0] || null); setPreview(null); }} data-testid="import-file-input" />
          </label>
          {!preview ? (
            <button className="btn-primary" disabled={!file || busy} onClick={() => run(false)} data-testid="import-preview-btn">{busy ? "Checking…" : "Preview import"}</button>
          ) : (
            <div className="space-y-4" data-testid="import-preview">
              <Summary s={preview.summary} />
              {preview.summary.error > 0 && <p className="flex items-center gap-2 text-xs text-rose-300"><WarningCircle size={14} /> Rows with errors will be skipped. Fix them in the sheet and upload again.</p>}
              <RowsTable rows={preview.rows} />
              <div className="flex flex-wrap gap-2">
                <button className="btn-primary" disabled={!valid || busy} onClick={() => run(true)} data-testid="import-confirm-btn">{busy ? "Importing…" : `Import ${valid} row(s)`}</button>
                <button className="btn-ghost" onClick={reset} data-testid="import-cancel-btn">Choose another file</button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
